// EmailTemplatesView — admin edita os templates de email transacional.
//
// Layout: 3 colunas em xl (lista | editor | preview), com preview sticky pra
// ficar sempre visível enquanto edita o HTML. Em telas menores cai pra 2
// colunas (lista + editor com preview abaixo).
//
// Editor de HTML usa syntax highlighting (VS Code dark) via CodeEditor.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Code2,
  Eye,
  Loader2,
  Mail,
  RefreshCw,
  Save,
  Send,
  Type
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import { Switch } from "@app/ui/components/switch";
import {
  api,
  ApiError,
  type EmailTemplateRow,
  type EmailTemplateVariable
} from "./api";
import { CodeEditor } from "./CodeEditor";
import { openAlert, openConfirm, openPrompt } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

const phase = signal<"loading" | "ready" | "error">("loading");
const templates = signal<EmailTemplateRow[]>([]);
const errMsg = signal<string | null>(null);
const selectedId = signal<string | null>(null);

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const r = await api.listEmailTemplates();
    templates.value = r.templates;
    if (!selectedId.value && r.templates.length > 0) {
      selectedId.value = r.templates[0].id;
    }
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function EmailTemplatesView() {
  useEffect(() => {
    void load();
  }, []);

  if (phase.value === "loading") {
    return (
      <div class="space-y-2">
        <Skeleton class="h-12 w-full" />
        <Skeleton class="h-64 w-full" />
      </div>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }

  const list = templates.value;
  const selected = list.find((t) => t.id === selectedId.value) ?? list[0];

  return (
    <div class="space-y-4">
      <div class="rounded-md border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-sm">
        <Mail className="mr-1 inline size-4 text-blue-600" />
        Edite os templates dos emails transacionais. Use{" "}
        <code class="rounded bg-muted px-1 text-xs">{"{{variavel}}"}</code>{" "}
        nos lugares que devem ser substituídos no envio. O preview à direita
        atualiza em tempo real conforme você digita.
      </div>

      <div class="grid gap-4 xl:grid-cols-[14rem_minmax(0,1fr)_minmax(0,1fr)] lg:grid-cols-[14rem_minmax(0,1fr)]">
        {/* Lista */}
        <aside class="space-y-1 lg:max-h-[80vh] lg:overflow-y-auto lg:pr-1">
          {list.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => (selectedId.value = t.id)}
              class={`flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors ${
                selected?.id === t.id
                  ? "border-primary bg-primary/5"
                  : "border-border bg-background hover:border-primary/40"
              }`}
            >
              <Mail
                className={`mt-0.5 size-4 shrink-0 ${
                  selected?.id === t.id ? "text-primary" : "text-muted-foreground"
                }`}
              />
              <div class="min-w-0 flex-1">
                <div class="truncate font-medium">{t.name}</div>
                <code class="block truncate text-[10px] text-muted-foreground">
                  {t.slug}
                </code>
              </div>
              {!t.isActive && (
                <span class="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[9px] font-semibold text-muted-foreground">
                  off
                </span>
              )}
            </button>
          ))}
        </aside>

        {/* Editor + Preview */}
        {selected && <EditorAndPreview key={selected.id} template={selected} />}
      </div>
    </div>
  );
}

function EditorAndPreview({ template }: { template: EmailTemplateRow }) {
  const [tab, setTab] = useState<"html" | "text" | "vars">("html");
  const [subject, setSubject] = useState(template.subject);
  const [html, setHtml] = useState(template.html);
  const [textBody, setTextBody] = useState(template.textBody);
  const [isActive, setIsActive] = useState(template.isActive);
  const [saving, setSaving] = useState(false);
  const dirty =
    subject !== template.subject ||
    html !== template.html ||
    textBody !== template.textBody ||
    isActive !== template.isActive;

  // Preview substitui placeholders pelos exemplos das variáveis
  const exampleVars = useMemo(() => {
    const out: Record<string, string> = {};
    for (const v of template.variables) out[v.key] = v.example;
    return out;
  }, [template.variables]);
  const previewSubject = substitute(subject, exampleVars);
  const previewHtml = substitute(html, exampleVars);
  const previewText = substitute(textBody, exampleVars);

  async function save() {
    setSaving(true);
    try {
      await api.updateEmailTemplate({
        id: template.id,
        subject,
        html,
        textBody,
        isActive
      });
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao salvar",
        message: friendlyError(e)
      });
    } finally {
      setSaving(false);
    }
  }

  async function sendTest(slug: string) {
    const to = await openPrompt({
      title: "Enviar teste do template",
      message:
        "Pra qual email queremos mandar? Vamos renderizar com dados mockados (valores fake, sem trocar senha nem gerar cobrança). URL aponta pro app real.",
      placeholder: "seu@email.com",
      confirmLabel: "Enviar"
    });
    if (!to) return;
    try {
      const r = await api.sendEmailTest(slug, to.trim());
      void openAlert({
        title: "Enviado",
        message: `Email de teste de "${template.name}" mandado pra ${r.to}.${
          r.providerMessageId ? `\n\nID Resend: ${r.providerMessageId}` : ""
        }`
      });
    } catch (e) {
      void openAlert({
        title: "Erro ao enviar",
        message: friendlyError(e)
      });
    }
  }

  async function reset() {
    const ok = await openConfirm({
      title: "Restaurar padrão?",
      message: `O conteúdo de "${template.name}" voltará ao default original do código. Suas alterações serão perdidas.`,
      confirmLabel: "Restaurar",
      danger: true
    });
    if (!ok) return;
    setSaving(true);
    try {
      const r = await api.resetEmailTemplate(template.id);
      setSubject(r.template.subject);
      setHtml(r.template.html);
      setTextBody(r.template.textBody);
      await load();
    } catch (e) {
      void openAlert({
        title: "Erro ao restaurar",
        message: friendlyError(e)
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      {/* Editor */}
      <div class="space-y-3 rounded-md border border-border bg-card p-5">
        <header>
          <h2 class="text-base font-bold">{template.name}</h2>
          {template.description && (
            <p class="mt-1 text-xs text-muted-foreground">
              {template.description}
            </p>
          )}
        </header>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">
            Assunto do email
          </label>
          <input
            type="text"
            value={subject}
            onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
            placeholder="Assunto"
            title="Assunto do email"
            class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>

        {/* Tabs */}
        <div class="flex flex-wrap gap-1 border-b border-border">
          <Tab active={tab === "html"} icon={Code2} label="HTML" onClick={() => setTab("html")} />
          <Tab active={tab === "text"} icon={Type} label="Texto plain" onClick={() => setTab("text")} />
          <Tab
            active={tab === "vars"}
            icon={AlertCircle}
            label={`Variáveis (${template.variables.length})`}
            onClick={() => setTab("vars")}
          />
        </div>

        {tab === "html" && (
          <div>
            <CodeEditor value={html} onChange={setHtml} language="markup" rows={22} />
            <p class="mt-1 text-[10px] text-muted-foreground">
              Use <code class="rounded bg-muted px-1">{"{{nomeDaVariavel}}"}</code>{" "}
              pros placeholders. Veja a aba "Variáveis" pra lista do que está
              disponível. Tab insere 2 espaços.
            </p>
          </div>
        )}

        {tab === "text" && (
          <div>
            <textarea
              value={textBody}
              onInput={(e) =>
                setTextBody((e.target as HTMLTextAreaElement).value)
              }
              rows={12}
              placeholder="Versão texto plain (fallback)"
              title="Texto plain"
              class="w-full rounded-md border border-border bg-background px-3 py-2 font-mono text-[12px] leading-relaxed"
            />
            <p class="mt-1 text-[10px] text-muted-foreground">
              Fallback pra clientes de email que não renderizam HTML.
            </p>
          </div>
        )}

        {tab === "vars" && <VariablesTab vars={template.variables} />}

        <div class="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
          <label class="flex items-center gap-2 text-xs">
            <Switch
              checked={isActive}
              onCheckedChange={(v) => setIsActive(v)}
              disabled={saving}
            />
            <span>Template ativo (se desligado, sistema usa o default do código)</span>
          </label>
          <div class="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => sendTest(template.slug)}
              disabled={saving}
              class="inline-flex items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/5 px-3 py-1.5 text-xs text-violet-700 hover:bg-violet-500/10 disabled:opacity-50 dark:text-violet-300"
              title="Renderiza com dados mockados e envia pra um email de teste"
            >
              <Send className="size-3" />
              Enviar teste
            </button>
            <button
              type="button"
              onClick={reset}
              disabled={saving}
              class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs hover:bg-accent disabled:opacity-50"
            >
              <RefreshCw className="size-3" />
              Restaurar padrão
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !dirty}
              class="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:brightness-110 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Save className="size-4" />
              )}
              Salvar
            </button>
          </div>
        </div>
      </div>

      {/* Preview — sticky em xl pra ficar sempre visível enquanto rola o editor */}
      <div class="space-y-2 xl:sticky xl:top-4 xl:self-start">
        <div class="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
          <Eye className="size-3.5" />
          Preview em tempo real
          <span class="ml-auto rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:text-emerald-300">
            Live
          </span>
        </div>
        <div class="rounded-md border border-border bg-background p-3">
          <div class="border-b border-border pb-2 text-xs">
            <span class="text-muted-foreground">Assunto:</span>{" "}
            <strong>{previewSubject}</strong>
          </div>
          {/* O próprio email tem bg (light ou dark via @media). Sem bg-white aqui
              pra que o dark mode do email respire — wrapper só dá clip/scroll. */}
          <div
            class="mt-3 max-h-[70vh] overflow-y-auto rounded-md border border-border"
            dangerouslySetInnerHTML={{ __html: previewHtml }}
          />
          {tab === "text" && (
            <details class="mt-2 text-xs">
              <summary class="cursor-pointer text-muted-foreground">
                Versão texto plain
              </summary>
              <pre class="mt-2 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md border border-border bg-muted/50 p-2 font-mono text-[11px]">
                {previewText}
              </pre>
            </details>
          )}
        </div>
        <p class="text-[10px] text-muted-foreground">
          Placeholders substituídos pelos valores de exemplo. Em produção,
          cada email recebe os valores reais do usuário.
        </p>
      </div>
    </>
  );
}

function Tab({
  active,
  icon: Icon,
  label,
  onClick
}: {
  active: boolean;
  icon: typeof Mail;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={`inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-xs font-medium transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

function VariablesTab({ vars }: { vars: EmailTemplateVariable[] }) {
  return (
    <div>
      <p class="mb-2 text-xs text-muted-foreground">
        Use os placeholders abaixo no HTML/Assunto/Texto. O sistema substitui
        automaticamente no momento do envio.
      </p>
      <ul class="space-y-1">
        {vars.map((v) => (
          <li
            key={v.key}
            class="flex items-start gap-3 rounded-md border border-border bg-background px-3 py-2 text-xs"
          >
            <code class="shrink-0 rounded bg-violet-500/10 px-1.5 py-0.5 font-mono text-[10px] text-violet-700 dark:text-violet-300">
              {"{{"}
              {v.key}
              {"}}"}
            </code>
            <div class="min-w-0 flex-1">
              <div>{v.description}</div>
              <div class="mt-0.5 text-[10px] text-muted-foreground">
                Ex: <span class="font-mono">{v.example}</span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function substitute(s: string, vars: Record<string, string>): string {
  return s.replace(/\{\{(\w+)\}\}/g, (_m, key) => vars[key] ?? `{{${key}}}`);
}
